import { useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowRightLeft,
  Loader2,
  PauseCircle,
  PlayCircle,
  Trash2,
  Undo2,
} from 'lucide-react';

import {
  useOrganizationLifecycleMutation,
  useOrganizationTransferOwnershipMutation,
  type OrgLifecycleAction,
} from '@/client/api/admin/organizations';
import { useOrgRoleCatalogQuery } from '@/client/api/admin/organizations.query';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import type { AdminOrganizationDetail } from '@/client/types/admin/users-orgs';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const LIFECYCLE_COPY: Record<
  OrgLifecycleAction,
  { label: string; title: string; body: string; destructive: boolean }
> = {
  activate: {
    label: 'Activate',
    title: 'Activate organization?',
    body: 'The organization becomes active again. This does not change its review or hold state.',
    destructive: false,
  },
  deactivate: {
    label: 'Deactivate',
    title: 'Deactivate organization?',
    body: 'The organization is switched off. Members lose access until it is reactivated. This does not change its review or hold state.',
    destructive: true,
  },
  delete: {
    label: 'Delete',
    title: 'Soft-delete organization?',
    body: 'This is a soft delete — the record is retained and can be restored with Undelete.',
    destructive: true,
  },
  undelete: {
    label: 'Undelete',
    title: 'Restore organization?',
    body: 'The organization record is restored. Note that member org-roles dropped at delete time are NOT restored and must be re-assigned.',
    destructive: false,
  },
};

const apiErrorCode = (error: unknown): string | null => {
  const err = error as any;
  const body = err?.data ?? err?.response?.data;
  const code =
    body?.data?.data?.error ??
    body?.data?.error ??
    body?.error ??
    null;
  return typeof code === 'string' ? code : null;
};

const DangerZoneTab = ({ org }: { org: AdminOrganizationDetail }) => {
  const lifecycleMutation = useOrganizationLifecycleMutation();
  const transferMutation = useOrganizationTransferOwnershipMutation();
  const roleCatalog = useOrgRoleCatalogQuery({ permissions: 0 });

  const [pendingAction, setPendingAction] = useState<OrgLifecycleAction | null>(null);

  const [search, setSearch] = useState('');
  const [newOwnerUid, setNewOwnerUid] = useState<number | null>(null);
  const [newOwnerLabel, setNewOwnerLabel] = useState('');
  const [outgoingRole, setOutgoingRole] = useState('admin');
  const [confirmTransfer, setConfirmTransfer] = useState(false);

  const isDeleted = org.deleted === 1;
  const isActive = org.active === 1;
  const hasOwner = (org.user_account?.uid ?? org.owner_uid ?? org.uid) != null;

  const runLifecycle = async () => {
    if (!pendingAction) return;
    const copy = LIFECYCLE_COPY[pendingAction];
    const toastId = toast.loading(`${copy.label}…`);
    try {
      await lifecycleMutation.mutateAsync({ oid: org.oid, action: pendingAction });
      toast.success(`${copy.label} succeeded`, {
        id: toastId,
        richColors: true,
        duration: 4000,
      });
    } catch (error: any) {
      toast.error(`${copy.label} failed`, {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    } finally {
      setPendingAction(null);
    }
  };

  const handleTransfer = async () => {
    if (newOwnerUid == null) return;
    setConfirmTransfer(false);
    const toastId = toast.loading('Transferring ownership…');
    try {
      await transferMutation.mutateAsync({
        oid: org.oid,
        new_owner_uid: newOwnerUid,
        role: outgoingRole || undefined,
      });
      toast.success('Ownership transferred', {
        id: toastId,
        description: `${newOwnerLabel || `uid ${newOwnerUid}`} is now the owner.`,
        richColors: true,
        duration: 5000,
      });
      setSearch('');
      setNewOwnerUid(null);
      setNewOwnerLabel('');
    } catch (error: any) {
      const code = apiErrorCode(error);
      const isNoOwner =
        code === 'no_current_owner' ||
        ((error as any)?.status === 409 &&
          String(getApiErrorMessage(error, '')).includes('no_current_owner'));

      toast.error(isNoOwner ? 'No current owner' : 'Transfer failed', {
        id: toastId,
        description: isNoOwner
          ? getApiErrorMessage(
              error,
              'This organization has no current owner (409 no_current_owner). Ownership cannot be transferred until an owner exists.'
            )
          : getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 8000,
      });
    }
  };

  return (
    <div className="grid max-w-3xl gap-6">
      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {isDeleted ? (
            <Undo2 className="h-4 w-4 text-blue-600" />
          ) : isActive ? (
            <PauseCircle className="h-4 w-4 text-amber-600" />
          ) : (
            <PlayCircle className="h-4 w-4 text-green-600" />
          )}
          Lifecycle
        </h2>
        <p className="text-xs text-gray-500">
          Activate / deactivate toggles access. Soft-delete retains the record. There is no
          hard erase for organizations.
        </p>
        <div className="flex flex-wrap gap-2">
          {isDeleted ? (
            <Button
              variant="outline"
              onClick={() => setPendingAction('undelete')}
              disabled={lifecycleMutation.isPending}
            >
              Undelete
            </Button>
          ) : (
            <>
              {isActive ? (
                <Button
                  variant="outline"
                  className="text-amber-700"
                  onClick={() => setPendingAction('deactivate')}
                  disabled={lifecycleMutation.isPending}
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={() => setPendingAction('activate')}
                  disabled={lifecycleMutation.isPending}
                >
                  Activate
                </Button>
              )}
              <Button
                variant="outline"
                className="text-red-600"
                onClick={() => setPendingAction('delete')}
                disabled={lifecycleMutation.isPending}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Soft-delete
              </Button>
            </>
          )}
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-amber-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-800">
          <ArrowRightLeft className="h-4 w-4" />
          Transfer ownership
        </h2>
        <p className="text-xs text-gray-500">
          Moves the organization to a new owner. The outgoing owner keeps membership with the
          role you select (defaults to <code>admin</code>). Orphaned orgs (no current owner)
          return <code>409 no_current_owner</code>.
        </p>
        {!hasOwner && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This organization currently has no owner. Transfer may fail with{' '}
            <code>no_current_owner</code>.
          </p>
        )}

        <div className="space-y-1">
          <Label>New owner</Label>
          <UserSearchSelect
            value={search}
            onChange={value => {
              setSearch(value);
              setNewOwnerUid(null);
              setNewOwnerLabel('');
            }}
            onSelect={user => {
              setNewOwnerUid(user.uid);
              const label =
                [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                user.email ||
                `uid ${user.uid}`;
              setNewOwnerLabel(label);
              setSearch(`${label} · uid ${user.uid}`);
            }}
            placeholder="Search users by name or email…"
          />
          {newOwnerUid != null && (
            <p className="text-xs text-gray-500">Selected uid {newOwnerUid}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="outgoing-role">Outgoing owner role</Label>
          <select
            id="outgoing-role"
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            value={outgoingRole}
            onChange={e => setOutgoingRole(e.target.value)}
          >
            {(roleCatalog.data ?? []).map(role => (
              <option key={role.ourid} value={role.role}>
                {role.role_friendly || role.role}
              </option>
            ))}
            {!(roleCatalog.data ?? []).some(r => r.role === outgoingRole) && (
              <option value={outgoingRole}>{outgoingRole}</option>
            )}
          </select>
        </div>

        <Button
          className="w-full"
          disabled={newOwnerUid == null || transferMutation.isPending}
          onClick={() => setConfirmTransfer(true)}
        >
          {transferMutation.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Transferring…
            </>
          ) : (
            'Transfer ownership…'
          )}
        </Button>
      </section>

      <AlertDialog
        open={Boolean(pendingAction)}
        onOpenChange={next => {
          if (!next) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction ? LIFECYCLE_COPY[pendingAction].title : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction ? LIFECYCLE_COPY[pendingAction].body : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                pendingAction && LIFECYCLE_COPY[pendingAction].destructive
                  ? 'bg-red-600 hover:bg-red-700'
                  : undefined
              }
              disabled={lifecycleMutation.isPending}
              onClick={e => {
                e.preventDefault();
                void runLifecycle();
              }}
            >
              {lifecycleMutation.isPending
                ? 'Working…'
                : pendingAction
                  ? LIFECYCLE_COPY[pendingAction].label
                  : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmTransfer} onOpenChange={setConfirmTransfer}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer ownership of {org.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {newOwnerLabel || `uid ${newOwnerUid}`} becomes the owner. The current owner (if
              any) remains a member with role <strong>{outgoingRole}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                void handleTransfer();
              }}
            >
              Transfer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DangerZoneTab;
